import { AlertV2, ButtonV2, DataGridRef, FilterV2, SearchV2, SortV2, TooltipV2 } from '@cimplico/components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GrowHeightContainer from '../components/GrowHeightContainer';
import BulkActionsErrorAlert from '../features/trial-balance/BulkActionErrors';
import TrialBalanceBulkActions from '../features/trial-balance/TrialBalanceBulkActions';
import TrialBalanceTable from '../features/trial-balance/TrialBalanceTable';
import TrialBalanceTabs from '../features/trial-balance/TrialBalanceTabs';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { useSyncExternalTrialBalance, useTrialBalance } from '../queries/use-trial-balance';
import { useUsers } from '../queries/use-users';
import { saveFilterV2, saveSortV2 } from '../slices/workpaperFilterSlice';
import { RootState } from '../store';
import { WorksheetStatusEnum, WorksheetWithAccount } from '../types/worksheet';
import { formatDate } from '../utilities/formatting';
import NavBreadcrumbs from '../components/nav-breadcrumbs/NavBreadcrumbs';
import PageTitle from '../components/PageTitle';
import DocumentBrowser from '../components/document-browser/DocumentBrowser';
import WorksheetPreview from '../features/worksheet-preview/WorksheetPreview';
import TrialBalanceProvider from '../features/trial-balance/TrialBalanceProvider';
import XeroDeepLink from '../components/XeroDeepLink';
import { TrialBalanceSyncReport, WorkpaperSourceEnum } from '../types/workpaper';
import { useIsMutating } from 'react-query';
import ProfitLoss from '../features/trial-balance/ProfitLoss';
import { useFirmContext } from '../providers/FirmProvider';
import plural from '../utilities/plural';

const STATUS_FILTERS = ['ALL', ...Object.values(WorksheetStatusEnum)];

const sortOptions = [
    { label: 'Code', value: 'account.code' },
    { label: 'Description', value: 'account.description' },
    { label: 'Account Type', value: 'account.type' },
    { label: 'Account Class', value: 'account.class' },
    { label: 'Report Code', value: 'account.reportCode' },
    { label: 'Ledger Amount', value: 'account.currentLedgerAmount' },
    { label: 'Workpaper Amount', value: 'account.currentWorkpaperAmount' },
    { label: 'Ledger Amount LY', value: 'account.lastYearLedgerAmount' },
    { label: 'Variance', value: 'account.variance' },
    { label: 'Variance (%)', value: 'account.variancePercent' },
    { label: 'Outstanding Queries', value: 'outstandingClientQueries' },
    { label: 'Outstanding Comments', value: 'outstandingInternalComments' },
    { label: 'Outstanding Checklist Items', value: 'outstandingChecklistItems' },
];

function TrialBalancePage(): JSX.Element {
    const [statusIndex, setStatusIndex] = useState(0);
    const [limit, setLimit] = useState(100);
    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState<string>();
    const [search, setSearch] = useState<string>();
    const [integrationError, setIntegrationError] = useState<{ name: string; message: string } | undefined>();
    const [syncReport, setSyncReport] = useState<TrialBalanceSyncReport>();
    const [bulkActionErrors, setBulkActionErrors] = useState<string[] | undefined>();
    const [sort, setSort] = useState<string>();
    const [query, setQuery] = useSearchParams();
    const { shortId } = useFirmContext();

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const tableRef = useRef<DataGridRef>(null);

    const workpaper = useWorkpaperContext();

    const savedFilters = useSelector((state: RootState) => state.workpaperFilters.value[workpaper.id]?.trialBalance);

    const syncTrialBalanceQuery = useSyncExternalTrialBalance(workpaper.id);

    const trialBalanceQuery = useTrialBalance({
        limit,
        page,
        filter,
        tab: statusIndex > 0 ? STATUS_FILTERS[statusIndex] : undefined,
        search,
        sort,
    });
    const usersQuery = useUsers({ page: 1, limit: 999, search: '', filter: '', sort: '' });

    const result = trialBalanceQuery.data;

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const selectedWorksheets = useMemo(
        () => result?.worksheets.filter((ws) => selectedIds.includes(ws.id)) ?? [],
        [result?.worksheets, selectedIds]
    );

    // Get any mutations
    const isRefreshing = useIsMutating({ mutationKey: 'refresh-trial-balance' });

    useEffect(() => {
        if (savedFilters?.filterQuery || savedFilters?.sortQuery) {
            setQuery((s) => {
                if (s.get('tab')) {
                    const tab = s.get('tab');
                    return `tab=${tab}&${savedFilters.filterQuery || ''}&${savedFilters.sortQuery || ''}`;
                }
                return `${savedFilters.filterQuery || ''}&${savedFilters.sortQuery || ''}`;
            });
        }
    }, []);

    function handleStatusTabChange(idx: number): void {
        setStatusIndex(idx);
        setPage(1);
        tableRef.current?.resetPagination();
    }

    /**
     * Clears any selected rows
     */
    function resetTable(): void {
        setSelectedIds([]);
        tableRef?.current?.clear();
    }
    function handleSelect(worksheets: WorksheetWithAccount[]) {
        setSelectedIds(worksheets.map((r) => r.id));
    }

    /**
     * Update the trial balance.
     * - If the trial balance comes from Xero call the api to auto refresh.
     * - If the trial balance is manually uploaded, redirect the user to an re-upload page
     *
     * Alerts the user if the Xero connection is invalid.
     */
    function handleUpdate(): void {
        if (workpaper.provider === 'XERO') {
            setSyncReport(undefined);
            syncTrialBalanceQuery.mutate(undefined, {
                onSuccess: (res) => {
                    setSyncReport(res.result);
                },
                onError: (res) => {
                    if (!res.response?.data) {
                        return;
                    }

                    const err = res.response.data;

                    if ('type' in err) {
                        setIntegrationError({ message: err.description, name: err.message });
                    }
                },
            });
        } else {
            navigate('update');
        }
    }

    // Temporary until a better way to populate saved filters with a list that has not loaded yet
    if (usersQuery.isLoading) {
        return <></>;
    }

    return (
        <>
            <DocumentBrowser locationSaveKey="browser-pos" />
            <div className="flex">
                <div className="w-full flex flex-col h-full gap-4">
                    <div className="border-b">
                        <NavBreadcrumbs />
                        <div className="flex items-start mb-2 flex-wrap gap-8">
                            <hgroup className="grow">
                                <PageTitle title="Trial Balance" />
                                <div className="flex items-center gap-2">
                                    <p className="text-gray-600 text-sm">As at {formatDate(workpaper.periodEnd)}</p>
                                    <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                                    <p className="capitalize text-gray-600 text-sm">{workpaper.accountingBasis?.toLowerCase()} Basis</p>
                                    {workpaper.provider === WorkpaperSourceEnum.XERO && (
                                        <>
                                            <div className="w-1 h-1 rounded-full bg-gray-400"></div>
                                            <XeroDeepLink label="View in Xero" reportId={1005} />
                                        </>
                                    )}
                                </div>

                                {/* Change Provider */}
                                <TrialBalanceProvider
                                    hasItems={Boolean(result?.totalItems)}
                                    lastSynced={result?.lastRefreshedAt}
                                    onXeroError={setIntegrationError}
                                />
                            </hgroup>

                            {/* The primary page action (Update) */}
                            {selectedWorksheets.length === 0 && (
                                <>
                                    <ProfitLoss
                                        ledgerProfitLoss={result?.ledgerProfitLoss}
                                        ledgerProfitLossComparative={result?.ledgerProfitLossComparative}
                                        workpaperProfitLoss={result?.workpaperProfitLoss}
                                    />
                                    <div data-intercom-target="trial-balance-update-button">
                                        <ButtonV2
                                            size="sm"
                                            label={'Update'}
                                            onClick={handleUpdate}
                                            isLoading={syncTrialBalanceQuery.isLoading}
                                            isDisabled={syncTrialBalanceQuery.isLoading || workpaper.readOnly}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Bulk actions */}
                            {selectedWorksheets.length > 0 && (
                                <TrialBalanceBulkActions
                                    worksheets={selectedWorksheets}
                                    onUpdate={handleUpdate}
                                    onBulkAction={resetTable}
                                    onBulkActionError={setBulkActionErrors}
                                />
                            )}
                        </div>

                        {/* Alert any bulk action errors */}
                        {bulkActionErrors && (
                            <div className="mb-4">
                                <BulkActionsErrorAlert
                                    errors={bulkActionErrors}
                                    onClose={(): void => {
                                        setBulkActionErrors(undefined);
                                        resetTable();
                                    }}
                                />
                            </div>
                        )}

                        {/* Alert a Xero connection error */}
                        {integrationError && (
                            <AlertV2
                                title={integrationError.name}
                                description={integrationError.message}
                                onClose={(): void => setIntegrationError(undefined)}
                                type="warn"
                                className="mb-4"
                            >
                                <AlertV2.Action
                                    label="Reconnect to Xero"
                                    onClick={(): void =>
                                        navigate(`/${shortId}/entities/${workpaper.entity.id}?tab=integrations&focusedIntegration=xero`)
                                    }
                                />
                                <AlertV2.ExternalLink
                                    href={'https://support.cimplico.com/en/collections/7176340-workpapers-integrations'}
                                    label="Learn more"
                                />
                            </AlertV2>
                        )}
                        {syncReport && <SyncResultsAlert results={syncReport} onClose={(): void => setSyncReport(undefined)} />}

                        {/* Quick status filters */}
                        <div data-intercom-target="trial-balance-tabs">
                            <TrialBalanceTabs onChange={handleStatusTabChange} selectedTabIndex={statusIndex} />
                        </div>
                    </div>
                    {/* FILTER */}
                    <GrowHeightContainer className="flex items-center gap-2" dataIntercomTarget="trial-balance-filter-search-sort">
                        <FilterV2
                            onChange={({ queryString }): void => {
                                setFilter(queryString);
                                dispatch(saveFilterV2({ workpaperId: workpaper.id, section: 'trialBalance', query: queryString }));
                            }}
                        >
                            {statusIndex === 0 && (
                                <FilterV2.CheckboxFilter
                                    options={[
                                        { label: 'Unprepared', value: WorksheetStatusEnum.UNPREPARED },
                                        { label: 'In Progress', value: WorksheetStatusEnum.IN_PROGRESS },
                                        { label: 'Awaiting Review', value: WorksheetStatusEnum.AWAITING_REVIEW },
                                        { label: 'Reviewed', value: WorksheetStatusEnum.REVIEWED },
                                        { label: 'Excluded', value: WorksheetStatusEnum.EXCLUDED },
                                    ]}
                                    id="status"
                                    label="Status"
                                />
                            )}
                            <FilterV2.CheckboxFilter
                                id="account.class"
                                label="Account Class"
                                options={[
                                    { label: 'Asset', value: 'ASSET' },
                                    { label: 'Liability', value: 'LIABILITY' },
                                    { label: 'Revenue', value: 'REVENUE' },
                                    { label: 'Expense', value: 'EXPENSE' },
                                    { label: 'Equity', value: 'EQUITY' },
                                ]}
                            />
                            <FilterV2.TextFilter id="account.reportCode" label="Report Code" />
                            <FilterV2.NumericFilter id="account.ledgerAmount" label="Ledger Amount" />
                            <FilterV2.NumericFilter id="account.workpaperAmount" label="Workpaper Amount" />
                            <FilterV2.NumericFilter id="account.ledgerAmountLY" label="Ledger Amount LY" />
                            <FilterV2.NumericFilter id="account.variance" label="Variance" />
                            <FilterV2.NumericFilter id="account.variancePercent" label="Variance (%)" isPercentage />
                            <FilterV2.ToggleFilter id="unreconciled" label="Unreconciled" prefix="is" />
                            <FilterV2.ToggleFilter id="outstandingComments" label="Outstanding Comments" />
                            <FilterV2.ToggleFilter id="outstandingQueries" label="Outstanding Queries" />
                            <FilterV2.ToggleFilter id="outstandingChecklistItems" label="Outstanding Checklist Items" />
                            <FilterV2.SelectFilter
                                id="assigned"
                                label="Assigned"
                                values={[]}
                                options={usersQuery.data?.users?.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` })) ?? []}
                            />
                        </FilterV2>
                        <SearchV2 placeholder="Search accounts..." onChange={setSearch} />
                        <SortV2
                            options={statusIndex === 0 ? [{ label: 'Status', value: 'status' }, ...sortOptions] : sortOptions}
                            defaultValue="account.code"
                            defaultDirection="asc"
                            onChange={({ queryString }): void => {
                                setSort(queryString);
                                dispatch(saveSortV2({ workpaperId: workpaper.id, section: 'trialBalance', query: queryString }));
                            }}
                        />
                    </GrowHeightContainer>

                    {/* TABLE */}
                    <div className="pb-2 min-w-0">
                        <TrialBalanceTable
                            tableRef={tableRef}
                            totalItems={result?.totalItems}
                            worksheets={result?.worksheets}
                            isLoading={trialBalanceQuery.isLoading || trialBalanceQuery.isFetching || isRefreshing > 0}
                            onPageChange={setPage}
                            onLimitChange={setLimit}
                            onSelect={handleSelect}
                            onUpdate={handleUpdate}
                        />
                    </div>
                </div>
                <WorksheetPreview />
            </div>
        </>
    );
}

type SyncResultsAlertProps = {
    results: TrialBalanceSyncReport;
    onClose: () => void;
};

function SyncResultsAlert({ results, onClose }: SyncResultsAlertProps): JSX.Element {
    const { id: workpaperId } = useWorkpaperContext();
    // Combine results and chart results
    const details = results.details;
    const chartDetails = results.chartResults?.details;

    const created = details.created;
    const excluded = details.excluded;

    // Find the matching chart reflection
    const reflection = results.chartResults?.workpaperSyncResults.find((wsr) => wsr.workpaperId === workpaperId);
    const erroredAccounts = reflection?.accounts.filter((a) => a.result === 'error');

    const updated = new Map(
        [
            ...(details.updated ?? []),
            ...(chartDetails?.updated ?? []).filter((u) => !erroredAccounts?.find((ea) => ea.entityAccountId === u.id)),
        ].map((obj) => [obj.id, obj])
    );
    const deleted = new Map(
        [
            ...(details.deleted ?? []),
            ...(chartDetails?.deleted ?? []).filter((u) => !erroredAccounts?.find((ea) => ea.entityAccountId === u.id)),
        ].map((obj) => [obj.id, obj])
    );

    /**
     * Formats a message informing the user how many occurrences of an action
     *
     * @param count Number of times the action was performed
     * @param action The action
     */
    function msg(items: { id: string; code: string | null; name: string }[], action: string): JSX.Element {
        return (
            <label>
                <b>{items.length}</b> {plural(items.length, 'account was', 'accounts were')}{' '}
                <div className={`inline-block font-bold ${items.length > 0 ? 'underline decoration-dotted cursor-pointer' : ''}`}>
                    <TooltipV2 disabled={items.length === 0} message={items.map((item) => `${item.code} - ${item.name}`)}>
                        {action}
                    </TooltipV2>
                </div>
            </label>
        );
    }

    return (
        <>
            {reflection && reflection.result === 'error' && (
                <AlertV2
                    className="mb-4"
                    type="error"
                    title="There were issues syncing chart of account details to this workpaper."
                    description={erroredAccounts?.map((a) => (
                        <label key={a.id}>
                            <b>
                                {a.code} - {a.name}
                            </b>
                            : {a.errorMessage}
                        </label>
                    ))}
                />
            )}
            <AlertV2
                className="mb-4"
                onClose={onClose}
                type="success"
                title="Successfully refreshed the trial balance"
                description={[
                    msg(created, 'created'),
                    msg(Array.from(updated.values()), 'updated'),
                    msg(excluded, 'excluded'),
                    msg(Array.from(deleted.values()), 'deleted'),
                ]}
            />
        </>
    );
}

export default TrialBalancePage;
